import { Locale } from '@flysas-tech/book-value-stream-models';
import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next, Trans as i18nTrans } from 'react-i18next';
import { LANGUAGE_CODES, reverseLanguageCode } from 'shared';

type ReactI18NextChild = React.ReactNode | Record<string, unknown>;

declare global {
  namespace React {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface HTMLAttributes<T> {
      children?: ReactI18NextChild | Iterable<ReactI18NextChild>;
    }
  }
}

export const i18nNamespace = {
  Booking: 'booking',
  Common: 'common',
  MMB: 'mmb',
  Assistance: 'assistance',
} as const;
type i18nNamespace = (typeof i18nNamespace)[keyof typeof i18nNamespace];

// Had to duplicate the function from shared here since importing it didn't work due to
// circular dependencies between i18n and common packages.
const isLocalhost = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
};

const topLevelDomainLocales: Record<string, Locale> = {
  se: Locale.SV_SE,
  no: Locale.NO_NO,
  dk: Locale.DA_DK,
  fi: Locale.EN_FI,
};

export const getLocale = (): Locale => {
  const hostname = location.hostname;
  const topLevelDomain = hostname.split('.').pop();

  const topLevelDomainLocale = topLevelDomain ? topLevelDomainLocales[topLevelDomain] : undefined;

  if (topLevelDomainLocale) {
    return topLevelDomainLocale;
  }

  const pathname = location.pathname;

  const localePattern = /^\/([a-z]{2}-[a-z]{2})\//;

  const match = pathname.match(localePattern);

  const locale = match?.[1];

  if (locale) {
    let languageCode = Object.values(Locale).find(code => code === locale);
    if (languageCode) {
      return languageCode;
    } else {
      const reversedLocale = reverseLanguageCode(locale as Locale);
      languageCode = Object.values(Locale).find(code => code === reversedLocale);
      if (languageCode) {
        return languageCode;
      }
    }
  }

  return isLocalhost() ? getConfigLocale() : Locale.EN_GB;
};

const getConfigLocale = (): Locale => {
  const storageConfig = sessionStorage.getItem('micro-frontend-config');
  return (storageConfig && JSON.parse(storageConfig).locale) ?? Locale.EN_GB;
};

export const getLanguage = () => {
  const locale = getLocale();
  const language = locale.split('-')[0];

  return language ?? LANGUAGE_CODES.EN;
};

type i18nProps = {
  baseUrl: string;
  namespaces: i18nNamespace[];
  language?: string;
};

export const initI18n = async ({ baseUrl, namespaces, language }: i18nProps): Promise<void> => {
  return new Promise((resolve, reject) => {
    i18n
      .use(HttpApi)
      .use(initReactI18next)
      .init(
        {
          backend: {
            loadPath: `${baseUrl.replace(/\/$/, '')}/locales/{{lng}}/{{ns}}.json`,
          },
          lng: (language ?? getLanguage()).replace('no', 'nb'),
          fallbackLng: 'en',
          defaultNS: 'booking',
          ns: namespaces,
          interpolation: {
            // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
            escapeValue: false,
          },
        },
        () => {
          resolve();
        }
      );

    i18n.on('failedLoading', function (language) {
      if (language === 'en') {
        reject();
      }
    });
  });
};

export const t = i18n.t;

export const Trans = i18nTrans;
